import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { black, darkGrey, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  headerImage: {
    display: 'block',
    width: '100%',
  },
  subHeader: {
    margin: '20px 0 15px',
  },
  text: {
    display: 'inline-block',
    marginBottom: 15,
  },
  title: {
    marginBottom: 0,
  },
  subtitle: {
    fontWeight: 'normal',
    fontStyle: 'italic',
  },
  reportTitle: {
    width: '100%',
    padding: '10px 0',
    borderTop: `1px solid ${black}`,
    borderBottom: `1px solid ${black}`,
    marginBottom: 20,
    fontWeight: 'bold',
    fontSize: 20,
    lineHeight: 1.2,
    color: darkGrey,
  },
  reportText: {
    fontSize: 20,
    lineHeight: 1.2,
    color: darkGrey,
    marginBlockStart: '10px',
    marginBlockEnd: '10px',
  },
}));

const InterpretationPage19 = (): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_5.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].INTERPRETATION_STEP_5.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 5:</Typography>
          <Typography variant="h3">Create Interpretation Report</Typography>
          <Typography variant="h5" className={classes.subHeader}>
            Axumin Interpretation Report
          </Typography>
          <Typography variant="body1" className={classes.text}>
            This resource can assist with providing appropriate information regarding your patient’s Axumin scan. It is
            not a guide or instructions, nor is it intended to replace the independent medical judgment of the provider.
            Blue Earth Diagnostics, Inc. does not provide nor imply any medical advice regarding patient care or patient
            management. It is the provider’s responsibility to accurately complete and submit necessary information.
          </Typography>
          <Typography variant="h5" className={classes.title}>
            Example template for an interpretation report
          </Typography>
          <Typography variant="h5" className={classes.subtitle}>
            Please see downloadable resources section for full interpretation report example
          </Typography>
          <Typography className={classes.reportTitle}>AXUMIN PET OF THE MID-THIGH TO SKULL BASE</Typography>
          <p className={classes.reportText}>
            [Text in
            <i> italics </i>
            denotes recommended but not essential information to record].
          </p>
          <p className={classes.reportText}>
            <b>DIAGNOSIS: </b>
            (eg, Stage and state of prostate cancer)
          </p>
          <p className={classes.reportText}>
            <b>EXAMINATION: </b>
            PET/CT or PET/MRI
          </p>
          <p className={classes.reportText}>
            <i>
              <b>EQUIPMENT: </b>
              PET camera (make and model)
            </i>
          </p>
          <p className={classes.reportText}>
            <b>AGENT: </b>
            Axumin
          </p>
          <p className={classes.reportText}>
            <b>HISTORY: </b>
            Clinical question; date of diagnosis; prior treatment (eg, prostatectomy, radiation therapy, other
            locoregional treatment, hormonal therapy, chemotherapy, other systemic treatments); current treatment;
            current/recent PSA levels [showing suspicion of recurrence; PSA Doubling Time (or comment if this is not
            available)]; Gleason Score (initial); prior imaging/correlative studies; notable clinical symptoms (e.g.
            pain; loss of ROM).
          </p>
          <p className={classes.reportText}>
            <strong>PROCEDURE: </strong>
            Administered activity; injection site (right or left); oral or IV contrast (if applicable);
            extravasation/tracer retention (if applicable). Record patient compliance with recommended scan preparation
            (ie, exercise, fasting, voiding).
            <i> Time of injection.</i>
          </p>
          <p className={classes.reportText}>
            <b>ACQUISITION: </b>
            Uptake time.
            <i> Axial extent of scan; minutes per bed position.</i>
          </p>
          <p className={classes.reportText}>
            <b>FINDINGS: </b>
            Record any findings, with reference to the following regions: local recurrence; loco-regional recurrence;
            distant disease. Record focality, size, and image slice number of any abnormal findings, and describe
            abnormal uptake relative to blood pool and bone marrow.
          </p>
          <p className={classes.reportText}>
            (Note: Avoid using terms like &quot;metabolic&quot; or &quot;hypermetabolism&quot;; rely on terms like
            uptake, activity, avidity, or accumulation).
          </p>
          <p className={classes.reportText}>
            <i>
              Normal findings; muscle uptake; liver uptake; pancreas uptake; incidental findings; SUV
              <sub>max</sub>
              &nbsp;(lesion); SUV
              <sub>mean</sub>
              &nbsp;(background).
            </i>
          </p>
          <p className={classes.reportText}>
            <i>Include images/snapshots of findings if possible.</i>
          </p>
          <p className={classes.reportText}>
            The report should read like a consultation, not a &quot;summary of hotspots.&quot; The document should avoid
            leaving open questions and, in those cases in which additional investigation may be warranted, should
            provide thoughts on next diagnostic steps.
            <sup>1</sup>
          </p>

          <p className={classes.reportText}>
            <b>IMPRESSION: </b>
            Record an abbreviated summary of relevant findings and, if possible, answer clinical question.
          </p>
          <p className={classes.reportText}>
            <b>RECOMMENDATIONS: </b>
            <i>(eg, additional testing, clinical correlation)</i>
          </p>
        </div>
      </div>
    </>
  );
};

export default InterpretationPage19;
